import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, Clock, ShieldCheck, X } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/store/auth';
import { api } from '@/lib/api-client';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import type { Specialty, Seniority } from '@parecer/shared';

interface PendingAddress {
  cep?: string;
  street?: string;
  number?: string;
  complement?: string;
  district?: string;
  city?: string;
  state?: string;
}

interface PendingOnboarding {
  bio?: string;
  specialties?: Specialty[];
  linkedinUrl?: string;
  cpf?: string;
  cnpj?: string;
  oab?: string;
  oabState?: string;
  oabCardFileUrl?: string;
  oabRegularityCertFileUrl?: string;
  professionalPhotoUrl?: string;
  address?: PendingAddress;
  acceptedEthicsCode?: boolean;
  acceptedPlatformTerms?: boolean;
  declaredNoImpediment?: boolean;
  acceptedAt?: string;
  createdAt?: string;
}

interface LawyerProfile {
  userId: string;
  name: string;
  email: string;
  oab?: string;
  oabState?: string;
  oabVerified?: boolean;
  oabVerifiedAt?: string;
  bio?: string;
  linkedinUrl?: string;
  specialties?: Specialty[];
  seniority?: Seniority;
  certifications?: Array<{ id: string; verified?: boolean }>;
}

interface LawyerPricingEntry {
  taskType: string;
  basePrice: number;
  active: boolean;
}

/**
 * On the first lawyer login after signup, flush any onboarding data the signup
 * form stashed in localStorage (bio, specialties, linkedinUrl — fields that
 * aren't Cognito attrs). The PostConfirmation Lambda only creates the profile
 * skeleton; this hook fills in the rest.
 */
function useFlushPendingLawyerOnboarding(): void {
  const email = useAuthStore((s) => s.user?.email);
  const role = useAuthStore((s) => s.user?.role);
  const ranRef = useRef(false);
  const { toast } = useToast();

  useEffect(() => {
    if (ranRef.current) return;
    if (role !== 'lawyers' || !email) return;
    const key = `pendingLawyerOnboarding:${email.toLowerCase()}`;
    let raw: string | null = null;
    try {
      raw = window.localStorage.getItem(key);
    } catch {
      return;
    }
    if (!raw) return;
    ranRef.current = true;

    let pending: PendingOnboarding;
    try {
      pending = JSON.parse(raw) as PendingOnboarding;
    } catch {
      window.localStorage.removeItem(key);
      return;
    }

    const body: Record<string, unknown> = {};
    if (pending.bio) body['bio'] = pending.bio;
    if (pending.specialties?.length) body['specialties'] = pending.specialties;
    if (pending.linkedinUrl) body['linkedinUrl'] = pending.linkedinUrl;
    if (pending.cpf) body['cpf'] = pending.cpf;
    if (pending.cnpj) body['cnpj'] = pending.cnpj;
    if (pending.oab) body['oab'] = pending.oab;
    if (pending.oabState) body['oabState'] = pending.oabState;
    if (pending.oabCardFileUrl) body['oabCardFileUrl'] = pending.oabCardFileUrl;
    if (pending.oabRegularityCertFileUrl) {
      body['oabRegularityCertFileUrl'] = pending.oabRegularityCertFileUrl;
    }
    if (pending.professionalPhotoUrl) body['professionalPhotoUrl'] = pending.professionalPhotoUrl;
    if (pending.address && Object.keys(pending.address).length > 0) {
      body['address'] = pending.address;
    }
    if (pending.acceptedEthicsCode) body['acceptedEthicsCode'] = true;
    if (pending.acceptedPlatformTerms) body['acceptedPlatformTerms'] = true;
    if (pending.declaredNoImpediment) body['declaredNoImpediment'] = true;
    if (pending.acceptedAt) body['acceptedAt'] = pending.acceptedAt;
    if (Object.keys(body).length === 0) {
      window.localStorage.removeItem(key);
      return;
    }

    api
      .patch('/lawyers/me', body)
      .then(() => {
        window.localStorage.removeItem(key);
        toast({
          variant: 'success',
          title: 'Perfil completado',
          description: 'Bio e especialidades salvas. Edite em /lawyer/perfil quando quiser.',
        });
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('Failed to flush pending lawyer onboarding:', err);
      });
  }, [email, role, toast]);
}

interface ChecklistItem {
  key: string;
  label: string;
  done: boolean;
  pending?: boolean;       // human-in-the-loop step (e.g. admin must verify OAB)
  cta?: { label: string; to: string };
  helper?: string;
}

function buildChecklist(profile: LawyerProfile | undefined, pricing: LawyerPricingEntry[] | undefined): ChecklistItem[] {
  if (!profile) return [];
  const bioOk = (profile.bio?.length ?? 0) >= 200;
  const specsOk = (profile.specialties?.length ?? 0) >= 1;
  const seniorityOk = Boolean(profile.seniority);
  const hasCertifications = (profile.certifications?.length ?? 0) > 0;
  const pricingOk = (pricing ?? []).some((p) => p.active && p.basePrice > 0);
  const oabVerified = Boolean(profile.oabVerified);

  const items: ChecklistItem[] = [];

  items.push({
    key: 'bio',
    label: 'Escrever bio profissional (mín. 200 caracteres)',
    done: bioOk,
    ...(bioOk ? {} : { cta: { label: 'Escrever bio', to: '/lawyer/perfil' } }),
  });
  items.push({
    key: 'specialties',
    label: 'Selecionar especialidades',
    done: specsOk,
    ...(specsOk ? {} : { cta: { label: 'Escolher áreas', to: '/lawyer/perfil' } }),
  });
  items.push({
    key: 'seniority',
    label: 'Indicar nível de senioridade',
    done: seniorityOk,
    ...(seniorityOk ? {} : { cta: { label: 'Indicar nível', to: '/lawyer/perfil' } }),
  });
  items.push({
    key: 'certifications',
    label: 'Adicionar certificações ou especializações (opcional, recomendado)',
    done: hasCertifications,
    ...(hasCertifications
      ? {}
      : {
          cta: { label: 'Adicionar', to: '/lawyer/perfil' },
          helper: 'Eleva credibilidade e justifica preço-prêmio em áreas especializadas.',
        }),
  });
  items.push({
    key: 'pricing',
    label: 'Definir tabela de preços',
    done: pricingOk,
    ...(pricingOk ? {} : { cta: { label: 'Definir preços', to: '/lawyer/precos' } }),
  });
  items.push({
    key: 'oab',
    label: oabVerified ? 'OAB verificada' : 'OAB em verificação',
    done: oabVerified,
    ...(oabVerified
      ? {}
      : {
          pending: true,
          helper: 'Verificação manual pela equipe Parecer.ai. Você é notificado quando for liberada.',
        }),
  });

  return items;
}

/**
 * Detect a fresh OAB verification by comparing the server-side `oabVerifiedAt`
 * timestamp against a localStorage marker. If the timestamp is newer (or new),
 * we show a celebration banner. The banner is dismissable; dismissing it
 * writes the seen marker.
 */
function useFreshOabVerification(profile: LawyerProfile | undefined): {
  showBanner: boolean;
  dismiss: () => void;
} {
  const verifiedAt = profile?.oabVerifiedAt;
  const userId = profile?.userId;
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(false);
  }, [verifiedAt]);

  if (!verifiedAt || !userId || !profile?.oabVerified) return { showBanner: false, dismiss: () => {} };

  let seenAt: string | null = null;
  try {
    seenAt = window.localStorage.getItem(`oabVerifiedSeen:${userId}`);
  } catch {
    // localStorage unavailable — show banner once per session.
  }

  const isFresh = !seenAt || seenAt < verifiedAt;
  const dismiss = () => {
    try {
      window.localStorage.setItem(`oabVerifiedSeen:${userId}`, verifiedAt);
    } catch {
      // best effort
    }
    setDismissed(true);
  };

  return { showBanner: isFresh && !dismissed, dismiss };
}

export function LawyerDashboard() {
  useFlushPendingLawyerOnboarding();

  const userId = useAuthStore((s) => s.user?.userId);
  const { data: profile } = useQuery({
    queryKey: ['users', 'me'],
    queryFn: () => api.get<LawyerProfile>('/users/me'),
    enabled: Boolean(userId),
  });
  const { data: lawyerDetail } = useQuery({
    queryKey: ['lawyer', userId],
    queryFn: () => api.get<{ pricing?: LawyerPricingEntry[] }>(`/lawyers/${userId}`),
    enabled: Boolean(userId),
  });

  const { showBanner: showOabBanner, dismiss: dismissOabBanner } = useFreshOabVerification(profile);

  const checklist = buildChecklist(profile, lawyerDetail?.pricing);
  const remaining = checklist.filter((it) => !it.done && !it.pending);
  const allDone = remaining.length === 0;

  return (
    <AppShell role="lawyers">
      <div className="max-w-5xl">
        <h1 className="font-serif text-display-lg">Dashboard</h1>
        <p className="text-muted-foreground mt-2">Visão geral da sua atuação no Parecer.ai.</p>

        {showOabBanner && (
          <div className="mt-6 rounded-md border border-success/40 bg-success/5 p-4 flex items-start gap-3">
            <ShieldCheck className="h-5 w-5 text-success flex-none mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-foreground">Sua OAB foi verificada!</p>
              <p className="text-sm text-muted-foreground mt-1">
                A partir de agora seu perfil exibe o selo de verificação no marketplace público. Você
                aparece prioritariamente nas sugestões da IA e pode receber demandas abertas sem restrição.
                Enviamos também um email com a confirmação.
              </p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={dismissOabBanner}
              className="text-muted-foreground"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Fechar</span>
            </Button>
          </div>
        )}

        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Receita do mês', value: 'R$ 0,00' },
            { label: 'Tarefas ativas', value: '0' },
            { label: 'Avaliação média', value: '—' },
            { label: 'Taxa de aceite', value: '—' },
          ].map((m) => (
            <Card key={m.label}>
              <CardHeader>
                <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {m.label}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-serif text-3xl">{m.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Onboarding
              {profile && (
                <span className="text-sm font-normal text-muted-foreground">
                  {checklist.filter((c) => c.done).length} / {checklist.length} concluídos
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!profile && <p className="text-sm text-muted-foreground">Carregando…</p>}
            {profile && allDone && (
              <div className="rounded-md border border-success/30 bg-success/5 p-4 text-sm">
                <p className="font-medium text-success">Perfil completo!</p>
                <p className="text-muted-foreground mt-1">
                  Você já pode receber tarefas. Itens com verificação manual (OAB, certificações)
                  aparecem como pendentes até a equipe Parecer.ai validar.
                </p>
              </div>
            )}
            {profile && (
              <ul className="space-y-2">
                {checklist.map((item) => {
                  const Icon = item.done ? CheckCircle2 : item.pending ? Clock : Circle;
                  return (
                    <li
                      key={item.key}
                      className={cn(
                        'flex items-start gap-3 rounded-md border px-3 py-2.5 text-sm',
                        item.done
                          ? 'border-success/30 bg-success/5 text-muted-foreground line-through decoration-success/40'
                          : item.pending
                            ? 'border-warning/40 bg-warning/5'
                            : 'border-border bg-card',
                      )}
                    >
                      <Icon
                        className={cn(
                          'h-4 w-4 flex-none mt-0.5',
                          item.done
                            ? 'text-success'
                            : item.pending
                              ? 'text-warning'
                              : 'text-muted-foreground',
                        )}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground">{item.label}</p>
                        {item.helper && (
                          <p className="text-xs text-muted-foreground mt-0.5">{item.helper}</p>
                        )}
                      </div>
                      {item.cta && (
                        <Link
                          to={item.cta.to}
                          className="text-xs font-medium text-accent hover:underline whitespace-nowrap flex-none mt-0.5"
                        >
                          {item.cta.label} →
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
